/**
 * 级联筛选器字段默认值配置
 * 如果后端没有返回字段，使用这里的默认值进行赋值
 */

import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@/pages/dashboard/_config/constant';
import type { IFilterOptionDisplayOrderConfig } from '@/pages/dashboard/components/NewFilter/_type';
import type { TCascadeFilterMsgMap, ICascadeFieldsMapResult, IRadioOptionItem } from './_type';

/** 平台 system 级联：255 顶（若有）→ 固定置顶 os_id → 其余 name_ch 拼音序（与 Techland 等产品约定一致） */
export const PLATFORM_CASCADE_SYSTEM_OPTION_DISPLAY_ORDER: IFilterOptionDisplayOrderConfig =
  {
    idKey: 'code',
    sortLabelKey: 'name_en',
    topMostId: '255',
    pinnedIds: ['11', '12', '2', '21', '7', '5', '6', '25', '26', '98'],
    sortLocale: 'zh',
  };

export const CASCADE_FILTER_MSG_MAP: TCascadeFilterMsgMap = {
  "ua_network": {
    "all_info_cn": "展示所有买量渠道的去重用户数据",
    "all_info_en": "Display data results based on de-duplicated users for all Media Channel.",
    "lft_name_cn": "网络类型",
    "lft_name_en": "Network Type",
    "cascade_parent_child_cn": "买量渠道",
    "cascade_parent_child_en": "Media Channel",
    "parent_key": "network_type",
    "child_key": "ua_network",
    "tooltip_cn": "UA 买量渠道来源",
    "tooltip_en": "UA Media Channel Source"
  },
  "media_channel": {
    "all_info_cn": "展示所有买量渠道的去重用户数据",
    "all_info_en": "Display data results based on de-duplicated users for all Media Channel.",
    "lft_name_cn": "网络类型",
    "lft_name_en": "Network Type",
    "cascade_parent_child_cn": "买量渠道",
    "cascade_parent_child_en": "Media Channel",
    "parent_key": "network_type",
    "child_key": "media_channel"
  },
  "campaign": {
    "all_info_cn": "展示所有Campaign的去重用户数据",
    "all_info_en": "Display data results based on de-duplicated users for all Campaign.",
    "lft_name_cn": "Campaign类型",
    "lft_name_en": "Campaign Type",
    "cascade_parent_child_cn": "Campaign",
    "cascade_parent_child_en": "Campaign",
    "parent_key": "campaign_type",
    "child_key": "campaign",
    "childPanelWidth": 580,
  },
  "system": {
    "all_info_cn": "展示所有平台的去重用户数据",
    "all_info_en": "Display data results based on de-duplicated users for all Platforms.",
    "lft_name_cn": "平台类型",
    "lft_name_en": "Platform Type",
    "cascade_parent_child_cn": "平台",
    "cascade_parent_child_en": "Platform",
    "parent_key": "platform_type",
    "child_key": "system",
    "childPanelWidth": 460,
    "optionDisplayOrder": PLATFORM_CASCADE_SYSTEM_OPTION_DISPLAY_ORDER,
  },
};
/**
 * 级联筛选器字段默认值配置
 */
export const CASCADE_FIELD_DEFAULTS = {
  // radioOptions 默认值
  radioOptions: {
    text: {
      en: 'All',
      cn: '全部',
    },
    textCN: {
      en: '全部',
      cn: '全部',
    },
  },
};

/**
 * 生成级联筛选器兜底文案与 key
 * 规则：
 * 1. filterKey / name 仅用于生成兜底文案
 * 2. parentKey / childKey 仍由后端或映射配置决定
 */
const buildCascadeFallback = (
  item: any,
  filterKey: string | undefined,
  name: string | undefined,
) => {
  const nameEn = item?.name_en || item?.name || filterKey || name || '';
  const nameCn = item?.name_cn || item?.name || name || nameEn || filterKey || '';

  return {
    nameEn,
    nameCn,
    allInfoEn: `Display data results based on de-duplicated users for all ${nameEn}.`,
    allInfoCn: `展示所有${nameCn}的去重用户数据`,
    lftNameEn: `${nameEn} Type`,
    lftNameCn: `${nameCn}类型`,
    parentChildEn: nameEn,
    parentChildCn: nameCn,
  };
};

/**
 * 映射 radioOptions 数组
 * @param radioOptions - 后端返回的 radio_options 或前端已有的 radioOptions
 * @param locale - 语言环境
 * @param allInfo - allInfo 字段值（用于 info 字段的默认值）
 * @returns 映射后的 radioOptions 数组
 */
export const mapRadioOptions = (
  radioOptions: IRadioOptionItem[] | '' | undefined,
  locale: string,
  allInfo?: string
): IRadioOptionItem[] | '' => {
  if (!radioOptions || radioOptions.length === 0) {
    return ''; // 让组件内部处理默认值
  }

  const isEn = locale === LANGUAGE_EN;

  return radioOptions.map((radioItem: IRadioOptionItem) => ({
    value: radioItem.value,
    // text 字段：优先使用 text_en/text_cn，否则使用前端已有 text，最后使用配置默认值
    text: radioItem.text_en || radioItem.text_cn || radioItem.text || CASCADE_FIELD_DEFAULTS.radioOptions.text.en,
    // textCN 字段：优先使用 text_cn，否则使用前端已有 textCN，最后使用配置默认值
    textCN: radioItem.text_cn || radioItem.text_en || CASCADE_FIELD_DEFAULTS.radioOptions.textCN.cn,
    disabled: radioItem.disabled,
    // info 字段：优先使用 info_en/info_cn，否则使用前端已有 info，最后使用 allInfo
    info: (isEn && radioItem.info_en) 
      ? radioItem.info_en 
      : (radioItem.info_cn || radioItem.info || allInfo),
    // render 和 infoIcon 是前端字段
    render: radioItem.render,
    infoIcon: radioItem.info_icon || radioItem.infoIcon,
  }));
};

/**
 * 映射级联筛选器的所有字段
 * @param item - 后端返回的配置项
 * @param locale - 语言环境
 * @param isNewCascadeFilter - 是否是新级联筛选器
 * @param filterKey - 筛选器的 key，用于从 CASCADE_FILTER_MSG_MAP 获取默认值
 * @param name - 筛选器的名称，用于 rtName 为空时的默认值
 * @returns 映射后的字段对象
 */
export const mapCascadeFields = (
  item: any,
  locale: string,
  isNewCascadeFilter: boolean,
  filterKey?: string,
  name?: string
): ICascadeFieldsMapResult => {
  if (!isNewCascadeFilter) {
    return {
      allInfo: '',
      lftName: '',
      rtName: '',
      parentChildName: '',
      parentKey: '',
      childKey: '',
      radioOptions: undefined,
    };
  }
  const isEn = locale === LANGUAGE_EN;
  // 从 CASCADE_FILTER_MSG_MAP 获取默认值（如果后端没有传值）
  const msgMap = filterKey ? CASCADE_FILTER_MSG_MAP[filterKey] : undefined;
  // 全局兜底：当未命中 CASCADE_FILTER_MSG_MAP 且后端没给字段时，按筛选器名称动态生成
  const fallback = buildCascadeFallback(item, filterKey, name);

  // allInfo 字段映射：后端 > MAP > 动态兜底
  const allInfo = getName(
    isEn,
    item.all_info_en || msgMap?.all_info_en || fallback.allInfoEn,
    item.all_info_cn || msgMap?.all_info_cn || fallback.allInfoCn,
    true,
  );

  // lftName 字段映射：后端 > MAP > 动态兜底
  const lftName = getName(
    isEn,
    item.lft_name_en || msgMap?.lft_name_en || fallback.lftNameEn,
    item.lft_name_cn || msgMap?.lft_name_cn || fallback.lftNameCn,
    true,
  );

  // rtName：优先使用入参名称，否则退回筛选器中英文名
  const rtName = name || getName(isEn, fallback.nameEn, fallback.nameCn, true);

  // parentChildName 字段映射：后端 > MAP > 动态兜底
  const parentChildName = getName(
    isEn,
    item.parent_child_name_en || msgMap?.cascade_parent_child_en || fallback.parentChildEn,
    item.parent_child_name_cn || msgMap?.cascade_parent_child_cn || fallback.parentChildCn,
    true,
  );

  // parentKey 和 childKey：仅使用后端或映射配置，未配置时交给组件默认推断
  const parentKey = item.parent_key || msgMap?.parent_key || '';
  const childKey = item.child_key || msgMap?.child_key || '';

  // radioOptions 字段映射
  const radioOptions = mapRadioOptions(
    item.radio_options || item.radioOptions,
    locale,
    allInfo
  );

  // tooltip 字段映射：后端 > MAP
  const tooltip = getName(
    isEn,
    item.tooltip_en || msgMap?.tooltip_en || '',
    item.tooltip_cn || msgMap?.tooltip_cn || '',
    true,
  );

  return {
    allInfo,
    lftName,
    rtName,
    parentChildName,
    parentKey,
    childKey,
    radioOptions,
    childPanelWidth: item.childPanelWidth || msgMap?.childPanelWidth,
    optionDisplayOrder:
      item.optionDisplayOrder ??
      item.option_display_order ??
      msgMap?.optionDisplayOrder,
    ...(tooltip ? { tooltip } : {}),
  };
};
